import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AddressResponse } from "../../api/addresses/types";
import type { DeliveryResponse } from "../../api/deliveries";
import { AddressMapActionList, buildAddressGoogleMapsSearchUrl, canUndoAddressMapDelivery } from "./AddressMapActionList";
import "./AddressContextMenu.css";

export interface AddressContextMenuProps {
    address: AddressResponse;
    position: { x: number; y: number };
    onClose: () => void;
    onMarkDelivered?: () => void;
    onUndoDelivery?: () => void;
    onHide?: () => void;
    onBlock?: () => void;
    onMove?: () => void;
    onEdit?: () => void;
    onCopy?: () => void;
    onDeliveryHistory?: () => void;
    selectedFlyerTypeId?: string | null;
    deliveries?: DeliveryResponse[];
    currentUserId?: string;
}

export const AddressContextMenu = ({
    address,
    position,
    onClose,
    onMarkDelivered,
    onUndoDelivery,
    onHide,
    onBlock,
    onMove,
    onEdit,
    onCopy,
    onDeliveryHistory,
    selectedFlyerTypeId,
    deliveries = [],
    currentUserId
}: AddressContextMenuProps): JSX.Element => {
    const { t } = useTranslation();
    const menuRef = useRef<HTMLDivElement>(null);
    const viewportPadding = 10;
    const [adjustedPosition, setAdjustedPosition] = useState(position);
    const googleMapsUrl = buildAddressGoogleMapsSearchUrl(address);

    const canUndo = canUndoAddressMapDelivery(selectedFlyerTypeId, onUndoDelivery, deliveries, currentUserId);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        // Close on Escape key
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscape);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [onClose]);

    // Adjust position after render so we can use measured menu size.
    useLayoutEffect(() => {
        const menu = menuRef.current;
        if (!menu) {
            setAdjustedPosition(position);
            return;
        }

        const rect = menu.getBoundingClientRect();
        const statusBar = document.querySelector(".map-status-bar");
        const statusBarHeight = statusBar ? statusBar.getBoundingClientRect().height : 56;
        const bottomReserve = statusBarHeight + 16;
        const maxX = Math.max(viewportPadding, window.innerWidth - rect.width - viewportPadding);
        const maxY = Math.max(viewportPadding, window.innerHeight - rect.height - bottomReserve);
        const x = Math.min(Math.max(position.x, viewportPadding), maxX);
        const y = Math.min(Math.max(position.y, viewportPadding), maxY);

        setAdjustedPosition({ x, y });
    }, [position.x, position.y]);

    return (
        <div
            ref={menuRef}
            className="address-context-menu"
            style={{
                left: `${adjustedPosition.x}px`,
                top: `${adjustedPosition.y}px`
            }}
            role="menu"
            aria-label={t("map.addressContextMenu.label")}
        >
            <div className="address-context-menu__header">
                <div className="address-context-menu__address-info">
                    <strong>{address.street}</strong>
                    {address.number && <span> {address.number}</span>}
                    {address.apartment && <span> {address.apartment}</span>}
                </div>
                <div className="address-context-menu__address-details">
                    {address.postalCode} {address.city}
                </div>
            </div>

            <div className="address-context-menu__divider" />

            <div className="address-context-menu__items">
                <AddressMapActionList
                    variant="context-menu"
                    address={address}
                    googleMapsUrl={googleMapsUrl}
                    isLoading={false}
                    markDeliveredDisabled={false}
                    canUndo={!!canUndo}
                    dismissAfterAction={onClose}
                    keepHostOpenOnDeliveryHistory={false}
                    dismissAfterMarkDelivered
                    onDeliveryHistory={onDeliveryHistory}
                    onMarkDelivered={onMarkDelivered}
                    onUndoDelivery={canUndo ? onUndoDelivery : undefined}
                    onHide={onHide}
                    onBlock={onBlock}
                    onMove={onMove}
                    onEdit={onEdit}
                    onCopy={onCopy}
                />
            </div>
        </div>
    );
};